import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, FolderKanban, Loader2, Lock, Plus } from "lucide-react";

import { getProject, listProjects, type Project } from "@/api/development_suite";
import {
  developmentSuiteKeys,
  isDeniedError,
  isNotFoundError,
  retryDevelopmentSuiteRequest,
} from "@/components/development_suite/developmentSuiteQueries";
import { ListPager, RequestFailure } from "@/components/development_suite/DevelopmentSuiteStates";
import { ProjectDetail } from "@/components/development_suite/ProjectDetail";
import { ProjectFormDialog } from "@/components/development_suite/ProjectFormDialog";
import { MunicipalitySwitcher } from "@/components/launch_pad/MunicipalitySwitcher";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { ModuleWorkspaceBody } from "@/components/module_summary/ModuleWorkspaceBody";
import { ModuleWorkspaceHeader } from "@/components/module_summary/ModuleWorkspaceHeader";
import { moduleWorkspaceShellClass } from "@/components/module_summary/moduleWorkspaceLayout";
import { SuiteTrackingLayout } from "@/components/workflow_tracking/SuiteTrackingLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useMunicipality } from "@/context/MunicipalityContext";
import { useUser } from "@/context/UserProvider";
import { useToast } from "@/hooks/useToast";
import { canUseDevelopmentSuiteIn, userHasDevelopmentSuite } from "@/lib/developmentSuiteAccess";
import { cn } from "@/lib/utils";


export function DevelopmentSuiteWorkspace() {
  const { currentUser } = useUser();
  const { municipality, municipalities, isLoading } = useMunicipality();

  // Callers gate on the same capability; this keeps the workspace from ever
  // rendering for a user the server has not opened it to.
  if (!userHasDevelopmentSuite(currentUser)) return null;

  const municipalityId = municipality?.id;
  const suiteOpen = canUseDevelopmentSuiteIn(currentUser, municipalityId);

  if (municipalityId != null && !isLoading && suiteOpen) {
    // Keyed so switching municipality returns to the overview on page 1.
    return <ProjectsWorkspace key={municipalityId} municipalityId={municipalityId} />;
  }

  return (
    <WorkspaceShell
      header={
        <ModuleWorkspaceHeader
          title="Development Suite"
          actions={<MunicipalitySwitcher />}
          density="compact"
        />
      }
    >
      {municipalityId != null && !isLoading ? (
        // No Project request is made for a municipality the suite is closed in.
        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-muted-foreground">
          <Lock className="h-8 w-8 opacity-40" aria-hidden />
          <p className="max-w-sm text-sm">
            Development Suite is not turned on for {municipality?.name ?? "this municipality"}. Choose another
            municipality above, or ask your team administrator if you need access.
          </p>
        </div>
      ) : (
        <SuiteTrackingLayout
          municipalitiesLoading={isLoading}
          municipalitiesCount={municipalities.length}
          municipalitySelected={false}
          emptyIcon={FolderKanban}
          selectMunicipalityMessage="Select a municipality above to view its projects."
          summaryStrip={null}
          sidebar={null}
          detail={null}
        />
      )}
    </WorkspaceShell>
  );
}

function WorkspaceShell({ header, children }: { header: ReactNode; children: ReactNode }) {
  return (
    <div className={cn(moduleWorkspaceShellClass, "flex-1 min-h-0")} data-testid="development-suite-workspace">
      {header}
      <ModuleWorkspaceBody className="!p-0" contentClassName="flex min-h-full flex-col">
        {children}
      </ModuleWorkspaceBody>
    </div>
  );
}

function HeaderActions({ children }: { children: ReactNode }) {
  return <div className="flex min-w-0 flex-wrap items-center gap-2">{children}</div>;
}

type ProjectDialogState = { project: Project | null } | null;

/**
 * Opens on the projects overview. Opening a project replaces the overview
 * with that project's workspace; "All projects" returns to the overview.
 */
function ProjectsWorkspace({ municipalityId }: { municipalityId: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);
  const [lastOpenedId, setLastOpenedId] = useState<number | null>(null);
  // One-shot: cleared once the row has focus so later refetches leave focus alone.
  const restoreFocusIdRef = useRef<number | null>(null);
  const [dialog, setDialog] = useState<ProjectDialogState>(null);
  const [deniedError, setDeniedError] = useState<Error | null>(null);
  const denied = deniedError != null;

  // A 403 from any load or save here (projects, a project, or its records)
  // means the suite is closed to this user in this municipality. Hold that
  // denial and drop cached Project data so nothing loaded earlier reappears.
  useEffect(() => {
    const holdDenial = (key: readonly unknown[] | undefined, error: unknown) => {
      if (key?.[0] !== "development_suite" || key[1] !== municipalityId) return;
      if (isDeniedError(error)) setDeniedError(error as Error);
    };
    const stopQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") {
        holdDenial(event.query.queryKey, event.action.error);
      }
    });
    const stopMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") {
        holdDenial(event.mutation.options.mutationKey, event.action.error);
      }
    });
    return () => {
      stopQueries();
      stopMutations();
    };
  }, [queryClient, municipalityId]);

  useEffect(() => {
    if (denied) queryClient.removeQueries({ queryKey: developmentSuiteKeys.all(municipalityId) });
  }, [denied, queryClient, municipalityId]);

  const listQuery = useQuery({
    queryKey: developmentSuiteKeys.projects(municipalityId, page),
    queryFn: () => listProjects(municipalityId, page),
    enabled: !denied,
    retry: retryDevelopmentSuiteRequest,
    placeholderData: (previous) => previous,
  });

  const projectQuery = useQuery({
    queryKey: developmentSuiteKeys.project(municipalityId, openId ?? 0),
    queryFn: () => getProject(municipalityId, openId as number),
    enabled: openId != null && !denied,
    retry: retryDevelopmentSuiteRequest,
  });

  // The control that opened a view unmounts with it, so move focus
  // deliberately: to the project name when opening, back to its row on return.
  useEffect(() => {
    if (openId != null) document.getElementById("project-workspace-name")?.focus();
  }, [openId]);

  useEffect(() => {
    const id = restoreFocusIdRef.current;
    if (openId != null || id == null) return;
    const row = document.querySelector<HTMLElement>(`[data-project-row="${id}"]`);
    if (!row) return;
    row.focus();
    restoreFocusIdRef.current = null;
  }, [openId, listQuery.isFetching]);

  const openProject = (id: number) => {
    setLastOpenedId(id);
    restoreFocusIdRef.current = id;
    setOpenId(id);
  };
  const showAllProjects = () => setOpenId(null);

  const handleSaved = (saved: Project) => {
    const created = dialog?.project == null;
    setDialog(null);
    queryClient.setQueryData(developmentSuiteKeys.project(municipalityId, saved.id), saved);
    void queryClient.invalidateQueries({ queryKey: ["development_suite", municipalityId, "projects"] });
    if (created) {
      setPage(1);
      openProject(saved.id);
    }
    toast({ title: created ? `Created ${saved.name}` : `Saved ${saved.name}` });
  };

  // React Query keeps the last data after a failed refetch; never show it as current.
  const projects = listQuery.isError ? [] : (listQuery.data?.projects ?? []);
  const pagination = listQuery.isError ? undefined : listQuery.data?.pagination;
  const openProjectData = openId != null && !projectQuery.isError ? projectQuery.data : undefined;

  const newProjectButton = (variant: "default" | "outline") =>
    denied ? null : (
      <Button size="sm" variant={variant} onClick={() => setDialog({ project: null })}>
        <Plus className="mr-1.5 h-4 w-4" aria-hidden />
        New project
      </Button>
    );

  const header =
    openId == null ? (
      <ModuleWorkspaceHeader
        title="Development Suite"
        density="compact"
        actions={
          <HeaderActions>
            {newProjectButton("default")}
            <MunicipalitySwitcher />
          </HeaderActions>
        }
      />
    ) : (
      <ModuleWorkspaceHeader
        title="Development Suite"
        hideTitle
        density="compact"
        meta={
          <>
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <h1
                id="project-workspace-name"
                tabIndex={-1}
                className="break-words text-lg font-semibold tracking-tight outline-none sm:text-xl"
              >
                {openProjectData?.name ?? (denied || projectQuery.isError ? "Project" : "Loading project…")}
              </h1>
              {openProjectData?.permits_count != null ? (
                <Badge variant="secondary" className="font-normal">
                  {countLabel(openProjectData.permits_count, "permit", "permits")}
                </Badge>
              ) : null}
              {openProjectData?.planning_records_count != null ? (
                <Badge variant="secondary" className="font-normal">
                  {countLabel(openProjectData.planning_records_count, "planning record", "planning records")}
                </Badge>
              ) : null}
            </div>
          </>
        }
        actions={
          <HeaderActions>
            <Button size="sm" variant="ghost" onClick={showAllProjects}>
              <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden />
              All projects
            </Button>
            {newProjectButton("outline")}
            <MunicipalitySwitcher />
          </HeaderActions>
        }
      />
    );

  return (
    <WorkspaceShell header={header}>
      {deniedError ? (
        <div className="mx-auto w-full max-w-5xl p-4 sm:p-6">
          <RequestFailure error={deniedError} what="Projects" onRetry={() => undefined} />
        </div>
      ) : openId == null ? (
        <section aria-labelledby="projects-heading" className="mx-auto w-full max-w-5xl space-y-4 p-4 sm:p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 id="projects-heading" className="text-xl font-semibold tracking-tight">
              Projects
            </h2>
            {pagination && pagination.total_count > 0 ? (
              <p className="text-sm text-muted-foreground">
                {pagination.total_count} {pagination.total_count === 1 ? "project" : "projects"}
              </p>
            ) : null}
          </div>

          {listQuery.isLoading ? (
            <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading projects…
            </p>
          ) : listQuery.isError ? (
            <RequestFailure error={listQuery.error} what="Projects" onRetry={() => void listQuery.refetch()} />
          ) : projects.length === 0 ? (
            <div className="rounded-lg border border-dashed px-4 py-10 text-center">
              <FolderKanban className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" aria-hidden />
              <p className="text-sm font-medium">No projects yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Create a project to group the permits and planning work for one development.
              </p>
              <div className="mt-4">{newProjectButton("outline")}</div>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border bg-card">
              <Table aria-labelledby="projects-heading" className="table-fixed">
                <colgroup>
                  <col />
                  <col className="hidden md:table-column" />
                  <col className="hidden w-56 lg:table-column" />
                  <col className="w-24" />
                  <col className="w-10" />
                </colgroup>
                <TableHeader className="bg-muted/40">
                  <TableRow className="hover:bg-transparent">
                    <TableHead scope="col" className="px-4">
                      Project
                    </TableHead>
                    <TableHead scope="col" className="hidden px-3 md:table-cell">
                      Location
                    </TableHead>
                    <TableHead scope="col" className="hidden px-3 lg:table-cell">
                      Responsible staff
                    </TableHead>
                    <TableHead scope="col" className="px-3 text-right">
                      Updated
                    </TableHead>
                    <TableHead scope="col" className="w-10">
                      <span className="sr-only">Open project</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map((project) => {
                    const location = [project.site_address, project.parcel_identifier].filter(Boolean).join(" · ");
                    return (
                      <TableRow
                        key={project.id}
                        className="group cursor-pointer focus-within:bg-muted/50"
                        data-state={project.id === lastOpenedId ? "selected" : undefined}
                        onClick={() => openProject(project.id)}
                      >
                        <TableCell className="px-4 py-3">
                          {/* The row handles the click; Enter on this button bubbles to it. */}
                          <button
                            type="button"
                            data-project-row={project.id}
                            aria-label={project.name}
                            className="flex w-full items-start gap-3 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <FolderKanban
                              className="mt-0.5 hidden h-4 w-4 shrink-0 text-muted-foreground sm:block"
                              aria-hidden
                            />
                            <span className="min-w-0">
                              <span className="line-clamp-2 break-words font-medium">{project.name}</span>
                              <span className="block truncate text-xs text-muted-foreground md:hidden">
                                {location || "No location yet"}
                              </span>
                            </span>
                          </button>
                        </TableCell>
                        <TableCell className="hidden truncate px-3 py-3 text-muted-foreground md:table-cell">
                          {location || "Not set"}
                        </TableCell>
                        <TableCell className="hidden truncate px-3 py-3 text-muted-foreground lg:table-cell">
                          {project.responsible_user?.email ?? "Not assigned"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap px-3 py-3 text-right text-xs text-muted-foreground">
                          {formatRelativeTime(project.updated_at)}
                        </TableCell>
                        <TableCell className="pr-4 text-muted-foreground group-focus-within:text-foreground group-hover:text-foreground">
                          <ChevronRight className="h-4 w-4" aria-hidden />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {pagination ? <ListPager pagination={pagination} label="Projects" onPageChange={setPage} /> : null}
        </section>
      ) : projectQuery.isLoading ? (
        <p className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading project…
        </p>
      ) : projectQuery.isError ? (
        <div className="mx-auto w-full max-w-3xl p-6">
          {isNotFoundError(projectQuery.error) ? (
            <p role="alert" className="text-sm text-muted-foreground">
              This project was not found in the selected municipality.
            </p>
          ) : (
            <RequestFailure error={projectQuery.error} what="This project" onRetry={() => void projectQuery.refetch()} />
          )}
        </div>
      ) : openProjectData ? (
        <ProjectDetail
          municipalityId={municipalityId}
          project={openProjectData}
          onEdit={() => setDialog({ project: openProjectData })}
        />
      ) : null}

      {dialog && !denied ? (
        <ProjectFormDialog
          municipalityId={municipalityId}
          project={dialog.project}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
        />
      ) : null}
    </WorkspaceShell>
  );
}

function countLabel(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}
